import React, { useState } from 'react';
import Type from 'prop-types';
import { Formik, Form } from 'formik';
import { noop } from 'lodash';
import { Button, IconButton, CircularProgress } from '@material-ui/core';
import { ArrowBackIos } from '@material-ui/icons';
import { validStep } from 'utils/customProps';
import { ru } from 'locale';
import Snackbar from '../Feedback/Snackbar';
import useStyles from './MultiStepForm.styles';

const MultiStepForm = ({
  steps,
  initialValues,
  getErrorMessage,
  onEachStepSubmit,
  onFinalSubmit,
}) => {
  const [state, setState] = useState({
    errorMessage: '',
    stepNumber: 0,
  });
  const [isPopupOpen, setPopupOpen] = useState(false);
  const classes = useStyles();

  const { errorMessage, stepNumber } = state;

  const isFirstStep = stepNumber === 0;
  const isLastStep = () => {
    return stepNumber === steps.length - 1;
  };
  const CurrentStep = steps[stepNumber];

  const showNextStep = ({ setFieldTouched }) => {
    const nextStepFieldNames = Object.keys(steps[stepNumber + 1].initialValues);
    nextStepFieldNames.forEach(fieldName => setFieldTouched(fieldName, false));
    setState(prevState => ({ ...prevState, stepNumber: prevState.stepNumber + 1 }));
  };

  const showPreviousStep = ({ setFieldTouched }) => () => {
    const previousStepFieldNames = Object.keys(steps[stepNumber - 1].initialValues);
    previousStepFieldNames.forEach(fieldName => setFieldTouched(fieldName, false));

    setState(prevState => ({ ...prevState, stepNumber: prevState.stepNumber - 1 }));
  };

  const handleError = error => {
    setState(prevState => ({ ...prevState, errorMessage: getErrorMessage(error) }));
    setPopupOpen(true);
  };

  const handlePopupClose = () => {
    setPopupOpen(false);
  };

  const handleSubmit = async (values, formikBag) => {
    if (errorMessage) {
      setState(prevState => ({ ...prevState, errorMessage: '' }));
    }

    handlePopupClose();

    try {
      await onEachStepSubmit(values);

      const currentStepSubmitHandler = steps[stepNumber].submitHandler;
      const responseData = await currentStepSubmitHandler(values);

      if (isLastStep()) {
        await onFinalSubmit(values, responseData?.data);
        formikBag.setSubmitting(false);
        formikBag.resetForm();
      } else {
        formikBag.setSubmitting(false);
        showNextStep(formikBag);
      }
    } catch (error) {
      formikBag.setSubmitting(false);
      handleError(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={CurrentStep.validationSchema}
      onSubmit={handleSubmit}
    >
      {formikBag => (
        <Form className={classes.form} noValidate>
          <CurrentStep {...formikBag} />
          {!isFirstStep && (
            <IconButton
              className={classes.prevButton}
              onClick={showPreviousStep(formikBag)}
              aria-label={ru.back}
            >
              <ArrowBackIos />
            </IconButton>
          )}

          <Snackbar
            open={isPopupOpen}
            onClose={handlePopupClose}
            message={errorMessage}
            variant="error"
          />

          <div className={classes.buttonWrapper}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.nextButton}
              disabled={formikBag.isSubmitting}
            >
              {CurrentStep.buttonLabel ?? ru.next}
            </Button>

            {formikBag.isSubmitting && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

MultiStepForm.propTypes = {
  initialValues: Type.object.isRequired, // eslint-disable-line react/forbid-prop-types
  getErrorMessage: Type.func.isRequired,
  onEachStepSubmit: Type.func,
  onFinalSubmit: Type.func,
  steps: Type.arrayOf(validStep).isRequired,
};

MultiStepForm.defaultProps = {
  onEachStepSubmit: noop,
  onFinalSubmit: noop,
};

export default MultiStepForm;
